import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

const WSContext = createContext(null);
const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws"
  : "ws://localhost:8001/api/ws";

export function WSProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const listenersRef = useRef([]);
  const reconnectTimer = useRef(null);

  const addListener = useCallback((fn) => {
    listenersRef.current.push(fn);
    return () => { listenersRef.current = listenersRef.current.filter(l => l !== fn); };
  }, []);

  const connect = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      const cookies = document.cookie;
      const tokenMatch = cookies.match(/access_token=([^;]+)/);
      const token = tokenMatch ? tokenMatch[1] : "";
      const url = token ? `${WS_BASE}?token=${token}` : WS_BASE;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsWsConnected(true);
        // Ping every 25s
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };

      ws.onclose = () => {
        setIsWsConnected(false);
        clearInterval(ws._pingInterval);
        reconnectTimer.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") return;
          listenersRef.current.forEach(fn => fn(data));
        } catch {}
      };
    } catch {}
  }, [user]);

  useEffect(() => {
    if (user) connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [user, connect]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <WSContext.Provider value={{ isOnline, isWsConnected, addListener }}>
      {children}
    </WSContext.Provider>
  );
}

export function useWS() {
  return useContext(WSContext);
}
